import {Moment} from 'moment-timezone'

import {RootCauseFailureAnalyses} from './analysis.types'
import {Iso4217, Iso8601} from './atomic.types'
import {Attachment} from './attachments.types'
import {SapDataDto, SapData} from './maintenanceNotification.types'
import {RunningTime, RunningTimeDto} from './runningTimes.types'
import {
  ShiftEventCategory,
  EquipmentData,
  Priority,
  StoppageDetails,
  InforWorkOrder,
  Id,
  SharedEventStructureDto,
  StatusWithCancellation,
  User,
  ShiftEventTaskDto,
  Assignees,
  DowntimeStoppageCode
} from './shiftHandover.types'
import {StoppageKind} from './shifthandoverStoppages.types'
import {TaskRepetition} from './taskRepetition.types'

export type StoppageDto = StoppageDetails &
  Id & {
    title: string
    eventType: StoppageKind
    equipment?: EquipmentData
    mainEquipment?: EquipmentData
    status: 'none'
    commentCount: number
    isSubmitted: boolean

    stoppageStart: Iso8601
    stoppageEnd?: Iso8601

    // optional
    isHighlighted?: boolean
    hasSalesImpact?: boolean
    cost?: number
    costCurrency?: Iso4217
    description: string | null
    processStage?: string
    priority?: Priority
    category?: ShiftEventCategory
    pxTrendTag?: string
    pxTrendStoppage?: RunningTimeDto
    rootCauseFailureAnalyses?: RootCauseFailureAnalyses[]
    inforWorkOrder?: InforWorkOrder
    originalSplitStoppageId?: string // This is not sent by BE but used for preparing split stoppage
    upmDepartmentId?: string
  }

export type DisabledFields = Partial<
  Record<
    keyof StoppageDto | 'stoppageCode' | 'stoppageReason' | 'equipmentNumber' | 'stoppageDateType',
    boolean
  >
>

export type HiddenFields = Partial<
  Record<keyof StoppageDto | 'stoppageCode' | 'stoppageReason' | 'equipmentNumber', boolean>
>

export type Stoppage = StoppageDetails &
  Id & {
    title: string
    eventType: StoppageKind
    equipment?: EquipmentData
    mainEquipment?: EquipmentData
    status: 'none'
    commentCount: number
    isSubmitted: boolean

    stoppageStart: Moment
    stoppageEnd?: Moment

    // optional
    isHighlighted?: boolean
    hasSalesImpact?: boolean
    cost?: number
    costCurrency?: Iso4217
    description: string | null
    processStage?: string
    priority?: Priority
    category?: ShiftEventCategory
    pxTrendTag?: string
    pxTrendStoppage?: RunningTime
    pxtrendStoppageId?: number
    rootCauseFailureAnalyses?: RootCauseFailureAnalyses[]
    inforWorkOrder?: InforWorkOrder
    stoppageGroup?: string // used for split stoppage group
    originalSplitStoppageId?: string // This is not sent by BE but used for preparing split stoppage
    upmDepartmentId?: string
  }

export type StoppageCode = {
  code: string
  description: string
}

export type DowntimeStoppageCodes = {
  kilnStoppageCodes: DowntimeStoppageCode[]
  rawMillStoppageCodes: DowntimeStoppageCode[]
  finishMillStoppageCodes: DowntimeStoppageCode[]
  alternativeFuelInstallationStoppageCodes: DowntimeStoppageCode[]
}

export type SystemChangeRequestData = {
  currentValue?: string
  newValue?: string
  functionId?: string
}

export type ParameterChangeData = {
  currentValue?: string
  newValue?: string
}

// Generic event status types. Used in Malfunction, Environment, Quality, HealthAndSafety
export const statusList = ['pending', 'ongoing', 'done'] as const
// Status used in Information and Idea
export const statusListWithNoneOption = ['pending', 'ongoing', 'done', 'none'] as const
export const statusWithNoneAndCancellation = [
  'pending',
  'ongoing',
  'done',
  'cancelled',
  'none'
] as const
export const approvalStatuses = ['approved', 'rejected', 'pending'] as const
// Status used for  ParameterChange along with the statusList
export const statusListApprovalStates = [
  'approved',
  'rejected',
  'waitingForApproval',
  'implemented',
  'implementedTemporary',
  'reverted'
] as const
// Approver states are used in the parameter change event type
export const statusListWithApproverStates = [...statusList, ...statusListApprovalStates]
export const eventStatusListWorkOrder = ['workOrderCreated'] as const

export type WorkOrderEventStatus = (typeof eventStatusListWorkOrder)[number]
export type StatusWithNone = (typeof statusListWithNoneOption)[number]
export type StatusWithNoneAndCancellation = (typeof statusWithNoneAndCancellation)[number]
export type StatusListWithApproverStates = (typeof statusListWithApproverStates)[number]
export type ApprovalStatus = (typeof approvalStatuses)[number]
export type EventStatus<T> = T | WorkOrderEventStatus

enum InspectionType {
  Unspecified = 'unspecified',
  Visual = 'visual',
  Thermal = 'thermal',
  Acoustic = 'acoustic'
}
type SpotInspectionMeasurement = {
  id: string
  name: string
  region: string
  unit: string
  value?: number
  threshold?: number
}
type SpotInspection = {
  id: string
  inspectionType: InspectionType
  spotInspectionMeasurements: SpotInspectionMeasurement[]
}

export type Approver = {
  user: User
  groupId?: string
  status: ApprovalStatus
}

type MalfunctionDto = SharedEventStructureDto & {
  eventType: 'malfunction'
  status: EventStatus<StatusWithCancellation>
}
type InformationDto = SharedEventStructureDto & {
  eventType: 'information'
  status: EventStatus<StatusWithNone>
}
type IdeaDto = SharedEventStructureDto & {
  eventType: 'idea'
  status: EventStatus<StatusWithNone>
}
type HealthAndSafetyDto = SharedEventStructureDto & {
  eventType: 'healthAndSafety'
  status: EventStatus<StatusWithNoneAndCancellation>
}
type EnvironmentDto = SharedEventStructureDto & {
  eventType: 'environment'
  status: EventStatus<StatusWithNoneAndCancellation>
}
export type ParameterChangeDto = SharedEventStructureDto & {
  eventType: 'parameterChange'
  status: EventStatus<StatusListWithApproverStates>
  approvers: Approver[]
  parameterChange?: ParameterChangeData
}
export type SystemChangeRequestDto = SharedEventStructureDto & {
  eventType: 'systemChangeRequest'
  status: EventStatus<StatusListWithApproverStates>
  parameterChange?: SystemChangeRequestData
  approvers: Approver[]
}
type QualityDto = SharedEventStructureDto & {
  eventType: 'quality'
  status: EventStatus<StatusWithNoneAndCancellation>
}
type SpotDto = SharedEventStructureDto & {
  eventType: 'spot'
  status: EventStatus<StatusWithNone>
  spotInspections: SpotInspection[]
}

// TODO find a better name then "info" (and change src//components/Info.tsx as well)
// TODO: pick this from common packages/common/types/shiftHandover.types.ts
export interface InfoDto {
  // username
  createdBy: User
  modifiedBy: string
  created: Iso8601
  lastModified: Iso8601
  lastModifiedStatus?: Iso8601
}

export type BaseEventDto =
  | MalfunctionDto
  | InformationDto
  | IdeaDto
  | HealthAndSafetyDto
  | EnvironmentDto
  | ParameterChangeDto
  | SystemChangeRequestDto
  | ShiftEventTaskDto
  | StoppageDto
  | QualityDto
  | SpotDto

export type ShiftEventDto = BaseEventDto &
  Id &
  InfoDto &
  SapDataDto & {
    attachments?: Attachment[]
  }

export type EventSearchDto = {
  currentPage: number
  events: ShiftEventDto[]
  totalItemCount: number
  totalPages: number
}

// this is what the yml defines
// export type EventSearchResult = {
//   events: ShiftEvent[]
//   currentPage: number
//   totalPages: number
// }
// and this what the backend returns
export type EventSearchResult = {
  currentPage: number
  events: ShiftEvent[]
  totalItemCount: number
  totalPages: number
}

export type Task = Omit<SharedEventStructure, 'schedule'> &
  Assignees & {
    dueDate: Moment
    eventType: 'task'
    status: StatusWithCancellation

    // optional
    toggleAssigneeNotification?: boolean
    repetitionInfo?: TaskRepetition | null
    shiftId?: number | null // TODO double check
    doNotStartBefore?: Moment
    isChecklistsCreationFailed?: boolean
    taskResolvedComment?: string
  }

export type BaseEvent =
  | Malfunction
  | Information
  | Idea
  | HealthAndSafety
  | Environment
  | ParameterChange
  | SystemChangeRequest
  | Task
  | Stoppage
  | Quality
  | Spot

interface Info {
  // username
  createdBy: User
  modifiedBy: string
  created: Moment
  lastModified: Moment
  lastModifiedStatus?: Moment
}

export type ShiftEvent = BaseEvent &
  Id &
  Info &
  SapData & {
    attachments?: Attachment[]
  }

export type Schedule = {
  startDate: Moment
  endDate?: Moment
}

export type SharedEventStructure = {
  category: ShiftEventCategory
  description: string
  title: string
  commentCount: number

  // optional
  isHighlighted?: boolean
  equipment?: EquipmentData
  mainEquipment?: EquipmentData
  processStage?: string
  priority?: Priority
  schedule?: Schedule
  inforWorkOrder?: InforWorkOrder
  upmDepartmentId?: string
}

export type Malfunction = SharedEventStructure & {
  eventType: 'malfunction'
  status: EventStatus<StatusWithCancellation>
}
export type Information = SharedEventStructure & {
  eventType: 'information'
  status: EventStatus<StatusWithNone>
}
export type Idea = SharedEventStructure & {
  eventType: 'idea'
  status: EventStatus<StatusWithNone>
}
export type HealthAndSafety = SharedEventStructure & {
  eventType: 'healthAndSafety'
  status: EventStatus<StatusWithCancellation>
}
export type Environment = SharedEventStructure & {
  eventType: 'environment'
  status: EventStatus<StatusWithCancellation>
}
export type ParameterChange = SharedEventStructure & {
  eventType: 'parameterChange'
  status: EventStatus<StatusListWithApproverStates>
  parameterChange?: ParameterChangeData
  approvers: Approver[]
  needsApproval: boolean
}
export type SystemChangeRequest = SharedEventStructure & {
  eventType: 'systemChangeRequest'
  status: EventStatus<StatusListWithApproverStates>
  parameterChange?: SystemChangeRequestData
  approvers: Approver[]
  needsApproval: boolean
}
export type Quality = SharedEventStructure & {
  eventType: 'quality'
  status: EventStatus<StatusWithCancellation>
}
export type Spot = SharedEventStructure & {
  eventType: 'spot'
  status: EventStatus<StatusWithNone>
  spotInspections: SpotInspection[]
}
